import { useCallback } from 'react';
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';
import { merge } from 'ts-deepmerge';
import { userConfigState, widgetPositionSelector } from '@/state/config';
import { hasUnreadMessagesSelector, isWidgetExpandedState, isWidgetOpenState, } from '@/state/session';
import { webChatStateState } from '@/state/socket';
import { useController } from './useController';
export const useWidgetState = () => {
    const setWidgetIsOpen = useSetRecoilState(isWidgetOpenState);
    const [isExpanded, setExpanded] = useRecoilState(isWidgetExpandedState);
    const setWebChatState = useSetRecoilState(webChatStateState);
    const widgetPosition = useRecoilValue(widgetPositionSelector);
    const hasUnreadMessages = useRecoilValue(hasUnreadMessagesSelector);
    const setUserConfig = useSetRecoilState(userConfigState);
    const { notifyWidgetClosed, notifyWidgetOpened, notifyNoUnreadMessages, notifyWidgetExpanded, } = useController();
    const openWidget = useCallback(() => {
        if (hasUnreadMessages) {
            notifyNoUnreadMessages();
        }
        setWidgetIsOpen(true);
        notifyWidgetOpened();
        if (isExpanded) {
            notifyWidgetExpanded(true);
        }
    }, [
        hasUnreadMessages,
        isExpanded,
        notifyNoUnreadMessages,
        notifyWidgetExpanded,
        notifyWidgetOpened,
        setWidgetIsOpen,
    ]);
    const closeWidget = useCallback(() => {
        if (widgetPosition !== 'embed') {
            setWidgetIsOpen(false);
        }
        setUserConfig((val) => merge({}, val, { auto_open: false }));
        if (widgetPosition !== 'embed') {
            setWebChatState("idle" /* WebChatState.Idle */);
        }
        else {
            setWebChatState("create" /* WebChatState.Create */);
        }
        notifyWidgetClosed();
    }, [
        widgetPosition,
        notifyWidgetClosed,
        setUserConfig,
        setWidgetIsOpen,
        setWebChatState,
    ]);
    const minimizeWidget = useCallback(() => {
        if (widgetPosition !== 'embed') {
            setUserConfig((val) => merge({}, val, { auto_open: false }));
            setWidgetIsOpen(false);
        }
        notifyWidgetClosed();
    }, [notifyWidgetClosed, setUserConfig, setWidgetIsOpen, widgetPosition]);
    const toggleWidgetExpanded = useCallback(async (value) => {
        notifyWidgetExpanded(value);
        setExpanded(value);
    }, [notifyWidgetExpanded, setExpanded]);
    return {
        openWidget,
        closeWidget,
        minimizeWidget,
        toggleWidgetExpanded,
    };
};
